import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AppModule, AuthSession, AuthUser, PermissionAction, PermissionMap, Role } from '@mexp/shared';
import { http, refreshSession, setAccessToken, setSessionHandlers } from '@/api/client';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: Status;
  user: AuthUser | null;
  permissions: PermissionMap | null;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  reloadPermissions: () => Promise<void>;
  can: (module: AppModule, action?: PermissionAction) => boolean;
  hasRole: (...roles: Role[]) => boolean;
}

/** Exported for the development-only portal preview, which supplies a stand-in session. */
export const AuthContext = createContext<AuthContextValue | null>(null);
export type { AuthContextValue };

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<PermissionMap | null>(null);
  const refreshTimer = useRef<number | undefined>(undefined);

  const signOutLocally = useCallback(() => {
    window.clearTimeout(refreshTimer.current);
    setAccessToken(null);
    setUser(null);
    setPermissions(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const applySession = useCallback(
    (session: AuthSession) => {
      setAccessToken(session.accessToken);
      setUser(session.user);
      setPermissions(session.permissions);
      setStatus('authenticated');
      // Renew quietly a minute before the access token expires.
      window.clearTimeout(refreshTimer.current);
      const delay = Math.max((session.expiresIn - 60) * 1000, 10_000);
      refreshTimer.current = window.setTimeout(() => {
        void refreshSession().then((next) => (next ? applySession(next) : signOutLocally()));
      }, delay);
    },
    [signOutLocally],
  );

  useEffect(() => {
    setSessionHandlers({
      onSession: applySession,
      onSignedOut: signOutLocally,
      onPasswordChangeRequired: () => setUser((u) => (u ? { ...u, mustChangePassword: true } : u)),
    });
    let cancelled = false;
    void refreshSession().then((session) => {
      if (cancelled) return;
      if (session) applySession(session);
      else setStatus('anonymous');
    });
    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimer.current);
    };
  }, [applySession, signOutLocally]);

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await http.post<AuthSession>('/auth/login', { email, password });
      queryClient.clear();
      applySession(session);
      return session.user;
    },
    [applySession, queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await http.post('/auth/logout');
    } finally {
      signOutLocally();
    }
  }, [signOutLocally]);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const session = await http.post<AuthSession>('/auth/change-password', { currentPassword, newPassword });
      applySession(session);
    },
    [applySession],
  );

  const reloadPermissions = useCallback(async () => {
    const me = await http.get<{ user: AuthUser; permissions: PermissionMap }>('/auth/me');
    setUser(me.user);
    setPermissions(me.permissions);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      permissions,
      login,
      logout,
      changePassword,
      reloadPermissions,
      can: (module, action = 'view') => !!permissions?.[module]?.[action],
      hasRole: (...roles) => !!user && roles.includes(user.role),
    }),
    [status, user, permissions, login, logout, changePassword, reloadPermissions],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The signed-in user (only use inside authenticated routes). */
export function useCurrentUser(): AuthUser {
  const { user } = useAuth();
  if (!user) throw new Error('No signed-in user');
  return user;
}
